import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, WifiOff, RefreshCw } from "lucide-react";
import { goToArticleCard } from "@/lib/scrollToArticle";
import LiveDot from "@/components/LiveDot";
import { useLang } from "@/lib/i18n";
import { liveLog } from "@/lib/liveLog";

const byNewest = (a, b) => String(b.published_at || "").localeCompare(String(a.published_at || "")) || String(b.id).localeCompare(String(a.id));

/**
 * «خبر جديد — اضغط للتحديث». New articles are buffered (never auto-scroll the
 * reader). Click on the home page: dispatch `articles:apply` with the frozen
 * batch, wait for the newest card to render, scroll to it and highlight it.
 * Elsewhere: open the newest article page (scroll position restored on back).
 * Also shows a quiet offline pill when the poller cannot refresh.
 */
export default function LiveUpdatesBanner() {
    const [buffer, setBuffer] = useState([]);
    const [phase, setPhase] = useState("idle"); // idle | applying | error
    const [offline, setOffline] = useState(false);
    const failures = useRef(0);
    const busy = useRef(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { p, t } = useLang();
    const isHome = /^\/(en|es|nl)?\/?$/.test(location.pathname);

    useEffect(() => {
        const onNew = (e) => {
            const items = (e.detail || []).filter((x) => x?.id);
            if (!items.length) return;
            setBuffer((prev) => {
                const seen = new Set(prev.map((x) => x.id));
                return [...prev, ...items.filter((it) => !seen.has(it.id))].sort(byNewest);
            });
        };
        const onStatus = (e) => {
            if (e.detail?.ok) { failures.current = 0; setOffline(false); }
            else { failures.current += 1; if (failures.current >= 2 || navigator.onLine === false) setOffline(true); }
        };
        const onOffline = () => setOffline(true);
        window.addEventListener("articles:new", onNew);
        window.addEventListener("articles:status", onStatus);
        window.addEventListener("offline", onOffline);
        return () => {
            window.removeEventListener("articles:new", onNew);
            window.removeEventListener("articles:status", onStatus);
            window.removeEventListener("offline", onOffline);
        };
    }, [isHome]);

    const apply = async () => {
        if (busy.current || !buffer.length) return;
        const batch = buffer.slice();          // frozen: later arrivals don't change the target
        const target = batch[0];
        busy.current = true; setPhase("applying");
        liveLog("banner.click", { n: batch.length, target: target.id, path: location.pathname });
        try {
            if (!isHome) {
                setBuffer((prev) => prev.filter((x) => !batch.some((b) => b.id === x.id)));
                setPhase("idle");
                navigate(`${p}/article/${target.slug}`);
                return;
            }
            const settled = new Promise((r) => {
                const t = setTimeout(r, 4000);
                window.addEventListener("articles:applied", () => { clearTimeout(t); r(); }, { once: true });
            });
            window.dispatchEvent(new CustomEvent("articles:apply", { detail: batch }));
            await settled;
            const ok = await goToArticleCard(target.id);
            if (!ok) { setPhase("error"); return; }
            setBuffer((prev) => prev.filter((x) => !batch.some((b) => b.id === x.id)));
            setPhase("idle");
        } finally { busy.current = false; }
    };

    if (offline && !buffer.length) {
        return (
            <div data-testid="live-offline-pill" dir="rtl" className="live-pill live-pill--offline" role="status">
                <WifiOff className="w-4 h-4" /><span>{t("offline")}</span>
            </div>
        );
    }
    if (!buffer.length) return null;
    const count = buffer.length;
    const label = phase === "applying" ? t("refreshing")
        : phase === "error" ? t("goFail")
        : `${count === 1 ? t("newOne") : t("newMany", count)} · ${isHome ? t("tapRefresh") : t("tapView")}`;

    return (
        <button onClick={apply} disabled={phase === "applying"} aria-busy={phase === "applying"}
            data-testid="live-updates-banner" data-count={count} data-phase={phase} dir="rtl"
            className={`live-pill live-pill--news ${phase === "error" ? "live-pill--error" : ""}`}>
            <LiveDot tone="white" testId="banner-live-dot" />
            {phase === "applying" ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
            <span data-testid="live-updates-label">{label}</span>
        </button>
    );
}
